// Per-schema (per-company) sales-quotation templates.
// Each SAP company schema prints on its own letterhead, field set, columns and terms.
// Resolved by matching the quotation's schema_name against `matches` (case-insensitive substring).
import { LOGO_2S, LOGO_MOZZAICO, LOGO_SBM, LOGO_888, LOGO_TILEEXPRESS } from "./quotationLogos";

// Column ids understood by QuotationPrint. Order in `columns` = print order.
//   no, description, item_code, size, qty, boxes, weight, availability,
//   unit_price, srp, discounted, net_total

export const QUOTATION_TEMPLATES = [
    {
        key: "2s_la_casa",
        matches: ["2S", "LA CASA", "LACASA"],
        company: {
            name: "2s Lacasa Blanca Inc",
            logo: LOGO_2S,
            address: ["BDO Account: 01086 800 3858", "All prices are inclusive of VAT"],
        },
        meta: ["date", "contact", "to", "project", "thru"],
        columns: ["no", "description", "item_code", "size", "qty", "unit_price", "discounted", "net_total"],
        totals: "simple",
        adhesive: false,
        payment: false,
        signatories: [],
        terms: [
            "This quotation is valid for fifteen (15) days upon receipt by customer, except for promo items that are valid only within their validity period.",
            "The quantities indicated are estimates only based on the given plan and/or actual measurement. The final quantity is the sole responsibility of the buyer. 2S LA CASA BLANCA INC. is not responsible for over/under shortage of tiles delivered based on the requested quantification. There is no refund for any excess materials.",
            "No downpayment, no Purchase Order to transact except those qualified accounts under credit terms as duly approved by Management. Payment must be made in full before delivery.",
            "Cheque payments must clear prior to delivery. Payment may be made in cash, by check, or through credit card, subject to existing company policies.",
            "Prices are subject to change without prior notice.",
            "Availability of stocks is subject to final confirmation based on verified PO or confirmation of Final Quotation. Indent items lead time is tentatively 30 to 60 days; customized art mosaics 75 to 90 working days (final layout must be signed by client and 2S LA CASA BLANCA INC. before closing the order).",
            "For indent items: 50% downpayment required, balance of 50% in full prior to delivery. For indent regular items: 20% downpayment.",
            "Delivery and unloading charges are subject to confirmation and cover ground floor only, within 10 meters of the truck. Beyond 10 meters, an additional Php 500.00 per person applies.",
            "Return and exchange must comply with the following: within seven (7) days from PO date (beyond 7 days, 10% surcharge); no return/exchange after 30 days; item in good order, in original box, still in current stock, not phased-out, not an indent item.",
            "Shade (Tonality) and Size Variation (Calibration) is inherent in manufactured tiles. Please inspect tiles before installation; complaints will not be entertained after tiles have been laid. Tonality per order may be requested for batches, subject to final confirmation.",
        ],
    },
    {
        key: "elittiles",
        matches: ["ELITTILE", "ELIT TILE", "ELITE"],
        company: {
            name: "Elittile Corporation",
            wordmark: { text: "ELITTILES", tagline: "PORCELAIN · MOSAICS · SLABS" },
            address: ["Specification, terms and conditions listed below"],
        },
        meta: ["date", "quotation_no", "ref", "to", "project", "location", "thru", "contact"],
        columns: ["no", "description", "item_code", "size", "qty", "unit_price", "discounted", "net_total"],
        totals: "split",
        adhesive: true,
        payment: true,
        signatories: [],
        reminders: [
            "For 60x60, use Tile Bonding; for 60x120, mosaics and pave (including pool mosaics), use Heavy-Duty tile adhesive.",
            "Tile grout is based on the preferred color of the client.",
            "Never use regular adhesive on mirror mosaics/tiles. For glass, use mosaic adhesive/silicone sealant; for mirror mosaic, silicone sealant is recommended.",
            "Recommended tile grout spacing is 3-4mm gap joint. Butt-joint at your own risk.",
            "Please inspect tiles BEFORE installation. Complaints will not be entertained after tiles have been installed.",
        ],
        terms: [
            "This quotation is valid for fifteen (15) days except for promo items (valid only within their validity period) and regular price which may change without prior notice.",
            "Quantities indicated are estimates based on the client's given plan and measurements. The final quantity is the sole responsibility of the buyer. There is no refund for any excess materials.",
            "Fifty (50) percent downpayment upon signing of this contract and full payment before delivery of items.",
            "Check payments must clear first before delivery.",
            "Cancellation of PO is subject to twenty (20) percent surcharge of the total amount (subject to review and approval).",
            "Availability of stock is subject to final confirmation and cannot be guaranteed without downpayment.",
            "Lead time for indent items is tentatively 30 to 60 days; exact arrival date cannot be guaranteed.",
            "Overdue accounts shall earn interest of one percent (1%) every ten (10) days or three percent (3%) per month.",
            "Unpicked and/or no-delivery-advisement paid items within thirty (30) days from PO date: payment and items are forfeited.",
            "Gate fees and permits during delivery are the responsibility of the client. A delivery charge may apply again if the first delivery was unsuccessful due to the client's oversight.",
        ],
    },
    {
        key: "mozzaico",
        matches: ["MOZZAICO", "MOSAICO"],
        company: {
            name: "Mosaico Arte Inc",
            logo: LOGO_MOZZAICO,
            address: [
                "4th Floor The Uptown Palazzo, 9th Ave. cor. 36th St.",
                "Bonifacio Global City, Taguig City · Mobile: 0917 531 0752",
            ],
        },
        meta: ["quotation_no", "ref", "to", "thru", "project", "project_address"],
        columns: ["no", "description", "item_code", "size", "qty", "boxes", "srp", "discounted", "net_total"],
        totals: "split",
        adhesive: true,
        payment: true,
        signatories: [
            { name: "MILDRED BURLAT", title: "Asst. Showroom Sales Mgr.", role: "Prepared by" },
            { name: "MONICA KWOK", title: "Consultant", role: "Reviewed / Noted by" },
            { name: "", title: "Signature of Client", role: "Conforme" },
        ],
        terms: [
            "This quotation is valid for fifteen (15) days except for promo items. Fifty (50) percent downpayment upon signing and full payment before delivery.",
            "Return and exchange of items (tiles, mosaics, vinyls) is allowed within thirty (30) days from date of purchase if found defective per international standards, intact and in original packaging. Not applicable to indent items including slabs; installed or broken items not accepted; promo and customized items excluded.",
            "Exchange within ten (10) days from purchase is free of charge; more than 10 but within 30 days is 10% from the actual selling price. Exchange item must be of equal or greater value; delivery charge for exchange is on the client.",
            "A daily storage fee applies for undelivered items beyond fifteen (15) days from PO date (Php 5.00 per sanitary accessory item; Php 10.00 per tile/mosaic/subway box). No release without settlement of the storage fee.",
            "Shade (Tonality) and Size Variation (Calibration) is inherent in tiles and mosaic products because they are made of natural materials. The approved sample shade may slightly vary from actual products delivered. Client must inspect items before installation.",
            "Damage caused by external factors beyond the Supplier's reasonable control (acts of God, war, labor disputes, transport disruptions, natural disasters and site accidents) will not be covered for replacement by MOZZAICO.",
            "Slabs: our priority is currently the supply side and not installation. Delivery mobilization by us is up to loading from our warehouse.",
            "Payment may be made in cash, by check, fund transfer, or credit card, subject to existing company policies.",
        ],
    },
    {
        key: "sbm",
        matches: ["SBM"],
        company: {
            name: "SBM Luxury Home Trading Inc",
            logo: LOGO_SBM,
            logoDark: true,
            address: [
                "Bldg. 1 Ground Flr. La Fuerza Compound, Bankal",
                "1233 City of Makati, NCR · TIN: 754-632-825-00002",
            ],
        },
        meta: ["date", "quotation_no", "agent", "to", "contact", "company", "company_address", "tin", "project", "delivery_address", "thru"],
        columns: ["no", "description", "item_code", "size", "qty", "weight", "availability", "unit_price", "discounted", "net_total"],
        totals: "simple",
        adhesive: false,
        payment: false,
        signatories: [],
        terms: [
            "This quotation is valid for fifteen (15) days upon receipt by customer, except for promo items.",
            "The quantities indicated are estimates based on the client's given plan and measurements. The final quantity is the sole responsibility of the buyer for any over or shortage of product delivered.",
            "Availability of stock is subject to final confirmation. Delivery charges shall vary depending on location.",
            "Lead time for indent items is tentatively 30 to 60 days; arrival date may vary due to port and customs conditions.",
            "Shade (Tonality) and Size Variation is inherent in manufactured tiles. Please inspect tiles before installation.",
        ],
    },
    {
        key: "888_tile",
        matches: ["888"],
        company: {
            name: "888 Tile Phil Corp",
            logo: LOGO_888,
            address: [
                "Unit G02, GF, Technopoint Bldg., Doña Julia Vargas Ave., Brgy. Ugong, Pasig City",
                "Landline: 623-4300 · Telefax: 477-3237",
                "All prices are E-VAT inclusive",
            ],
        },
        intro: "In line with your requirements, we are pleased to present our price quotation as follows:",
        meta: ["date", "to", "project", "thru"],
        columns: ["description", "item_code", "size", "qty", "srp", "discounted", "net_total", "remarks"],
        totals: "amount",
        adhesive: false,
        payment: false,
        signatories: [
            { name: "", title: "OIC / Sales Specialist", role: "Prepared by" },
            { name: "", title: "Signature Over Printed Name", role: "Conforme" },
        ],
        closing:
            "Thank you for the opportunity to do business with you. Should you have any questions, please do not hesitate to call us. Rest assured, your business will receive our utmost attention.",
        terms: [
            "This quotation is valid for fifteen (15) days. Prices and discounts are valid for this quotation only, and are subject to change without prior notice after the validity period.",
            "Availability of stocks is subject to final confirmation.",
            "No cancellation of Purchase Order.",
            "No return and exchange for items cut into special size and fabricated, or for indent-ordering items.",
            "Tonality per order may be requested or for batches, subject to final confirmation.",
            "Payment may be made in cash or check, subject to existing company policies, payable to BDO Acct. 888 Tile Phil Corp. – 01086-800-3793. A check does not produce the effect of payment until actually received and cashed. All prices are E-VAT inclusive.",
            "Delivery charge per truck and per delivery may apply and may vary depending on load and location. Delivery is up to the receiving area only.",
            "Moving delivery to a later date will not affect the payment schedule of the checks issued. Delays due to acts of God are acceptable.",
            "Delays caused by an unprepared unloading area are not the supplier's fault; any resulting adjustment means additional delivery charges.",
            "A 5% storage fee on undelivered items applies if items are not picked up or delivered on the scheduled date due to unavailability of the client's site/area.",
            "Items that do not pass the client's standard may be replaced with good-condition items, provided they are in the original box and not installed. Inspect items upon delivery; the supplier allows 7 days to replace the item in question (with invoice, receipt and client authorization).",
            "If PCR testing/PPE is required during delivery, the cost of testing and other requirements will be charged to the client.",
        ],
    },
    {
        key: "pedraverde",
        matches: ["PEDRAVERDE", "PEDRA VERDE", "TILE EXPRESS", "TILEEXPRESS"],
        company: {
            name: "Pedraverde Enterprise Inc",
            logo: LOGO_TILEEXPRESS,
            address: ["Specification, terms and conditions listed below"],
        },
        meta: ["date", "quotation_no", "ref", "to", "project", "location", "thru", "contact"],
        columns: ["no", "description", "item_code", "size", "qty", "unit_price", "discounted", "net_total"],
        totals: "split",
        adhesive: true,
        payment: true,
        reminders: [
            "For 60x60, use Tile Bonding; for 60x120, mosaics and pave (including pool mosaics), use Heavy-Duty tile adhesive.",
            "Tile grout is based on the preferred color of the client.",
            "Never use regular adhesive on mirror mosaics/tiles. For glass, use mosaic adhesive/silicone sealant; for mirror mosaic, silicone sealant is recommended.",
            "Recommended tile grout spacing is 3-4mm gap joint. Butt-joint at your own risk.",
            "Please inspect tiles BEFORE installation. Complaints will not be entertained after tiles have been installed.",
        ],
        signatories: [
            { name: "", title: "Senior Sales Manager", role: "Prepared by" },
            { name: "", title: "Name, Position & Signature", role: "Reviewed by" },
            { name: "", title: "Name, Position & Signature", role: "Noted by" },
            { name: "", title: "Signature of Client", role: "Conforme" },
        ],
        terms: [
            "This quotation is valid for fifteen (15) days except for promo items (valid only within their validity period); regular price may change without prior notice.",
            "Quantities indicated are estimates based on the client's given plan and measurements. The final quantity is the sole responsibility of the buyer for any over or shortage; no refund for excess materials.",
            "Fifty (50) percent downpayment upon signing of this contract and full payment before delivery of items.",
            "Check payments must clear first before delivery.",
            "Cancellation of PO is subject to twenty (20) percent surcharge of the total amount (subject to review and approval).",
            "Availability of stock is subject to final confirmation and cannot be guaranteed without downpayment.",
            "Lead time for indent items is tentatively 30 to 60 days; exact arrival date cannot be guaranteed.",
            "Overdue accounts shall earn interest of one percent (1%) every ten (10) days or three percent (3%) per month.",
            "Unpicked / no-delivery-advisement paid items within thirty (30) days from PO date: payment and items are forfeited.",
            "Gate fees and permits during delivery are the responsibility of the client; a delivery charge may apply again if the first delivery was unsuccessful due to the client's oversight.",
            "Return and exchange of items (tiles, mosaics, vinyls) within thirty (30) days from purchase if defective per international standards, intact and in original packaging — not applicable to slabs, indent, installed/broken, promo or customized items. Exchange within 7 days free; within 8–30 days 10% of selling price; exchange item must be of equal or greater value and is subject to delivery charge.",
            "A daily storage fee applies for undelivered items beyond fifteen (15) days from PO date (Php 5.00 per sanitary accessory; Php 10.00 per other sanitary/tile/mosaic/subway box). No release without settlement of the storage fee.",
            "Shade (Tonality) and Size Variation (Calibration) is inherent in tiles and mosaic products, which are made of natural materials; the approved sample shade may slightly vary from actual products. Client must inspect items before installation.",
            "Slabs: priority is currently the supply side, not installation. Delivery mobilization is up to loading from our warehouse; unloading is on the client's site only (subject to delivery-cost assessment, excluded from regular slab purchase). Warehouse pick-up requires six (6) persons and a centered steel rack; slabs are sintered stone and the risk of loss transfers to the client on pick-up.",
            "A notice to proceed with delivery within 7 days must be advised; afterwards the Purchase Order is automatically in effect for collection. This quotation automatically becomes a contract when accepted and confirmed by the client. Payment may be made in cash, check, fund transfer or credit card, subject to existing company policies.",
        ],
        closing:
            "Notes: Not applicable for indent and customized items. Arrival of imported goods depends on weather, customs and port situation. Recommended tile grout spacing is 3-4mm gap joint (butt-joint at your own risk). Please inspect tiles BEFORE installation — complaints will not be entertained after tiles have been installed.",
    },
];

// Generic fallback for any schema without a dedicated template.
export const GENERIC_TEMPLATE = {
    key: "generic",
    matches: [],
    company: { name: "", address: [] },
    meta: ["date", "quotation_no", "to", "project", "thru", "contact"],
    columns: ["no", "description", "item_code", "size", "qty", "unit_price", "discounted", "net_total"],
    totals: "simple",
    adhesive: false,
    payment: false,
    signatories: [],
    terms: [
        "This quotation is valid for fifteen (15) days upon receipt by customer, except for promo items.",
        "Quantities indicated are estimates; the final quantity is the sole responsibility of the buyer.",
        "Availability of stock is subject to final confirmation. Prices are subject to change without prior notice.",
        "Shade (Tonality) and Size Variation is inherent in manufactured tiles. Please inspect before installation.",
    ],
};

// Resolve by the SELLING company — the user's own company (or branch) captured on the
// quotation — NOT the supplier schema the stock was sourced from. e.g. a Mozzaico agent who
// inquired from the Dwellstone schema still prints on the Mozzaico letterhead.
// Priority: company, then branch. No supplier-schema fallback (that would show the wrong brand).
export function resolveQuotationTemplate({ company, branch, displayName } = {}) {
    for (const cand of [company, branch]) {
        const s = String(cand || "").toUpperCase();
        if (!s) continue;
        for (const t of QUOTATION_TEMPLATES) {
            if (t.matches.some((m) => s.includes(m))) return t;
        }
    }
    // Unknown company → generic letterhead, but keep the company name if we have it.
    return { ...GENERIC_TEMPLATE, company: { ...GENERIC_TEMPLATE.company, name: company || displayName || "" } };
}
